import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	CreateDocumentBody,
	CreateDocumentTemplateBody,
	CreateSignatureBody,
	DocumentSignature,
	DocumentTemplate,
	DocumentTemplateVersion,
	GenerateDocumentUploadUrlBody,
	GenerateDocumentUploadUrlResponse,
	ListDocumentTemplatesParams,
	ListDocumentTemplatesResponse,
	ListEntityDocumentsParams,
	ListEntityDocumentsResponse,
	ListRegistryDocumentsParams,
	ListRegistryDocumentsResponse,
	ListSignaturesResponse,
	ListTemplateAuthorsResponse,
	ListTemplateHistoryResponse,
	RegistryDocument,
	UpdateDocumentBody,
	UpdateDocumentStatusBody,
	UpdateDocumentTemplateBody,
} from './types/document'

export const documentsApi = createApi({
	reducerPath: 'documentsApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: [
		'RegistryDocument',
		'DocumentTemplate',
		'TemplateVersion',
		'TemplateAuthor',
		'TemplateHistory',
		'DocumentSignature',
		'EntityDocument',
	],
	endpoints: (builder) => ({
		// ── Registry ─────────────────────────────────────────

		listRegistryDocuments: builder.query<
			ListRegistryDocumentsResponse,
			ListRegistryDocumentsParams | void
		>({
			query: (params) => ({ url: '/documents', params: params ?? {} }),
			providesTags: (result) =>
				result
					? [
							...result.documents.map((d) => ({ type: 'RegistryDocument' as const, id: d.id })),
							{ type: 'RegistryDocument' as const, id: 'LIST' },
						]
					: [{ type: 'RegistryDocument' as const, id: 'LIST' }],
		}),

		getDocument: builder.query<RegistryDocument | null, string>({
			query: (id) => `/documents/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'RegistryDocument', id }],
		}),

		createDocument: builder.mutation<RegistryDocument | null, CreateDocumentBody>({
			query: (body) => ({ url: '/documents', method: 'POST', body }),
			invalidatesTags: (_r, _e, { entityType, entityId }) => [
				{ type: 'RegistryDocument', id: 'LIST' },
				{ type: 'EntityDocument', id: `${entityType.toLowerCase()}:${entityId}` },
			],
		}),

		generateDocumentUploadUrl: builder.mutation<
			GenerateDocumentUploadUrlResponse,
			GenerateDocumentUploadUrlBody
		>({
			query: (body) => ({ url: '/documents/upload-url', method: 'POST', body }),
		}),

		updateDocument: builder.mutation<
			RegistryDocument | null,
			{ id: string; body: UpdateDocumentBody }
		>({
			query: ({ id, body }) => ({ url: `/documents/${id}`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'RegistryDocument', id },
				{ type: 'RegistryDocument', id: 'LIST' },
				// The per-entity lists too, which is where a title is read most
				// often. Status and delete already did this; update did not, so a
				// rename reached the server and the card kept the old name until
				// the page was reloaded. Untargeted because the mutation is not
				// told which entity the document hangs off.
				{ type: 'EntityDocument' },
			],
		}),

		updateDocumentStatus: builder.mutation<
			RegistryDocument | null,
			{ id: string; body: UpdateDocumentStatusBody }
		>({
			query: ({ id, body }) => ({ url: `/documents/${id}/status`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'RegistryDocument', id },
				{ type: 'RegistryDocument', id: 'LIST' },
				{ type: 'EntityDocument' },
			],
		}),

		deleteDocument: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/documents/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'RegistryDocument', id },
				{ type: 'RegistryDocument', id: 'LIST' },
				{ type: 'EntityDocument' },
			],
		}),

		renderDocument: builder.mutation<RegistryDocument | null, string>({
			query: (id) => ({ url: `/documents/${id}/render`, method: 'POST' }),
			invalidatesTags: (_r, _e, id) => [{ type: 'RegistryDocument', id }],
		}),

		// ── Templates ────────────────────────────────────────

		listDocumentTemplates: builder.query<
			ListDocumentTemplatesResponse,
			ListDocumentTemplatesParams | void
		>({
			query: (params) => ({ url: '/documents/templates', params: params ?? {} }),
			providesTags: (result) =>
				result
					? [
							...result.templates.map((t) => ({ type: 'DocumentTemplate' as const, id: t.id })),
							{ type: 'DocumentTemplate' as const, id: 'LIST' },
						]
					: [{ type: 'DocumentTemplate' as const, id: 'LIST' }],
		}),

		getDocumentTemplate: builder.query<DocumentTemplate | null, string>({
			query: (id) => `/documents/templates/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'DocumentTemplate', id }],
		}),

		createDocumentTemplate: builder.mutation<DocumentTemplate | null, CreateDocumentTemplateBody>({
			query: (body) => ({ url: '/documents/templates', method: 'POST', body }),
			invalidatesTags: [{ type: 'DocumentTemplate', id: 'LIST' }],
		}),

		updateDocumentTemplate: builder.mutation<
			DocumentTemplate | null,
			{ id: string; body: UpdateDocumentTemplateBody }
		>({
			query: ({ id, body }) => ({
				url: `/documents/templates/${id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'DocumentTemplate', id },
				{ type: 'DocumentTemplate', id: 'LIST' },
			],
		}),

		deleteDocumentTemplate: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/documents/templates/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'DocumentTemplate', id },
				{ type: 'DocumentTemplate', id: 'LIST' },
			],
		}),

		listTemplateVersions: builder.query<{ versions: DocumentTemplateVersion[] }, string>({
			query: (id) => `/documents/templates/${id}/versions`,
			providesTags: (_r, _e, id) => [{ type: 'TemplateVersion', id }],
		}),

		listTemplateAuthors: builder.query<ListTemplateAuthorsResponse, void>({
			query: () => '/documents/templates/authors',
			providesTags: [{ type: 'TemplateAuthor', id: 'LIST' }],
		}),

		listTemplateHistory: builder.query<ListTemplateHistoryResponse, string>({
			query: (id) => `/documents/templates/${id}/history`,
			providesTags: (_r, _e, id) => [{ type: 'TemplateHistory', id }],
		}),

		// ── Signatures ───────────────────────────────────────

		listDocumentSignatures: builder.query<ListSignaturesResponse, string>({
			query: (id) => `/documents/${id}/signatures`,
			providesTags: (_r, _e, id) => [{ type: 'DocumentSignature', id }],
		}),

		createDocumentSignature: builder.mutation<
			DocumentSignature | null,
			{ id: string; body: CreateSignatureBody }
		>({
			query: ({ id, body }) => ({
				url: `/documents/${id}/signatures`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'DocumentSignature', id },
				{ type: 'RegistryDocument', id },
			],
		}),

		// ── Entity scope ─────────────────────────────────────

		listPropertyDocuments: builder.query<ListEntityDocumentsResponse, ListEntityDocumentsParams>({
			query: ({ entityId, ...params }) => ({
				url: `/properties/${entityId}/documents`,
				params,
			}),
			providesTags: (_r, _e, { entityId }) => [{ type: 'EntityDocument', id: `property:${entityId}` }],
		}),

		listDealDocuments: builder.query<ListEntityDocumentsResponse, ListEntityDocumentsParams>({
			query: ({ entityId, ...params }) => ({
				url: `/crm/deals/${entityId}/documents`,
				params,
			}),
			providesTags: (_r, _e, { entityId }) => [{ type: 'EntityDocument', id: `deal:${entityId}` }],
		}),

		listContactDocuments: builder.query<ListEntityDocumentsResponse, ListEntityDocumentsParams>({
			query: ({ entityId, ...params }) => ({
				url: `/crm/contacts/${entityId}/documents`,
				params,
			}),
			providesTags: (_r, _e, { entityId }) => [{ type: 'EntityDocument', id: `contact:${entityId}` }],
		}),
	}),
})

export const {
	useListRegistryDocumentsQuery,
	useGetDocumentQuery,
	useCreateDocumentMutation,
	useGenerateDocumentUploadUrlMutation,
	useUpdateDocumentMutation,
	useUpdateDocumentStatusMutation,
	useDeleteDocumentMutation,
	useRenderDocumentMutation,
	useListDocumentTemplatesQuery,
	useGetDocumentTemplateQuery,
	useCreateDocumentTemplateMutation,
	useUpdateDocumentTemplateMutation,
	useDeleteDocumentTemplateMutation,
	useListTemplateVersionsQuery,
	useListTemplateAuthorsQuery,
	useListTemplateHistoryQuery,
	useListDocumentSignaturesQuery,
	useCreateDocumentSignatureMutation,
	useListPropertyDocumentsQuery,
	useListDealDocumentsQuery,
	useListContactDocumentsQuery,
} = documentsApi
